// Import all components
import DevelopersOverview from '../pages/Dashboard/DevelopersOverview';
import CompanyInsights from '../pages/Dashboard/CompanyInsights';
import AuthorsInsights from '../pages/Dashboard/AuthorsInsights';
import Certifications from '../pages/Dashboard/Certifications';
import Overview from '../pages/Dashboard/Overview';
import DevFeed from '../pages/Dashboard/DevFeed';
import AiAssistant from '../pages/Assistant/AiAssistant';
import Repos from '../pages/Dashboard/Repos';
import TagsDashboard from '../pages/Dashboard/Tags';
import LegacyAiAdoptionRedirect from '../pages/Dashboard/LegacyAiAdoptionRedirect';
import {
  AiLeverageDevelopers,
  AiLeverageRepositories,
  AiLeverageProjects
} from '../pages/Dashboard/AiLeverageDashboard';
import Categories from '../pages/Dashboard/Categories';
import Retention from '../pages/Dashboard/Retention';
import Projects from '../pages/Dashboard/Projects';
import Authors from '../pages/Dashboard/Authors';
import DeveloperReport from '../pages/Dashboard/DeveloperReport';
import RepoReport from '../pages/Dashboard/RepoReport';
import ProjectReport from '../pages/Dashboard/ProjectReport';
import UserDetail from '../components/UserDetail/UserDetail';
import Settings from '../components/Settings/Settings';
import ChangePassword from '../components/Settings/ChangePassword';
import ManageUserAccess from '../components/Settings/ManageUserAccess';
import HistoricalAnalysis from '../components/Settings/HistoricalAnalysis';
import ProjectManagement from '../components/Settings/ProjectManagement';
import Configurations from '../components/Settings/Configurations';
import Tags from '../components/Settings/Tags';
import Developers from '../components/Settings/Developers';
import Repositories from '../components/Settings/Repositories';
import Integrations from '../components/Settings/Integrations';
import CustomerSupportPage from '../components/Support/SupportWorkspace';
import SelfHostedIntegrationConnect from '../pages/Authentication/SelfHostedIntegrationConnect';
import PlanConfiguration from '../pages/Dashboard/Payment/PlanConfiguration';
import CardManagement from '../pages/Dashboard/Payment/CardManagement';
import InviteUser from '../pages/Dashboard/Payment/InviteUser';
import PaymentSummary from '../pages/Dashboard/Payment/PaymentSummary';
import { ROLES, resolveUserRole } from './rolePermissions';

const resolveDashboardRole = userRole => {
  const normalizedRole = resolveUserRole(userRole);
  return normalizedRole === ROLES.DEMO_USER ? ROLES.VIEWER : normalizedRole;
};

export const hasAccess = (userRole, allowedRoles) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  return allowedRoles.includes(userRole);
};

/* ================= ROUTES ================= */
export const UNIFIED_ROUTES = [
  {
    path: '/',
    label: 'Executive Summary',
    pageSubtitle:
      'A unified view of engineering effort, ownership, and concentration.',
    icon: 'Insights',
    component: DevelopersOverview,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/dev-feed',
    label: 'DevFeed',
    pageSubtitle: 'See what’s happening — and why it matters',
    icon: 'DynamicFeed',
    component: DevFeed,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/ai-assistant',
    label: 'Ask AI',
    pageSubtitle:
      'Ask Gitzy to analyze GitMe data and show the right table, chart, or cohort.',
    icon: 'AutoAwesome',
    component: AiAssistant,
    allowWithoutCommits: true,
    hidePageHeader: true,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },

  /* AI INSIGHTS */
  {
    path: '/company-insights',
    label: 'Company Overview',
    parentLabel: 'Productivity Scores',
    icon: 'Business',
    component: CompanyInsights,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/developers-insights',
    label: 'Developers Overview',
    pageSubtitle:
      'AI-generated assessment of individual delivery health over the last 12 months.',
    parentLabel: 'Productivity Scores',
    icon: 'Groups',
    component: AuthorsInsights,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/certifications',
    label: 'Certifications',
    pageSubtitle:
      'Share-ready GitMe verified engineering recognitions with public proof records.',
    parentLabel: 'Productivity Scores',
    icon: 'WorkspacePremium',
    component: Certifications,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },

  /* PERFORMANCE */
  {
    path: '/performance-overview',
    label: 'Overview',
    pageSubtitle:
      'How engineering effort evolves across time, categories, and contributors.',
    parentLabel: 'Performance',
    icon: 'Leaderboard',
    component: Overview,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/developers',
    label: 'Developers',
    pageSubtitle:
      'A detailed view of developer effort distribution and trends over time.',
    parentLabel: 'Performance',
    icon: 'Assessment',
    component: Authors,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/repos',
    label: 'Repositories',
    pageSubtitle:
      'A detailed view of effort distribution and trends across repositories.',
    pageActions: [
      {
        label: 'Manage Repositories',
        href: 'https://panel.gitdotme.com/settings/repositories'
      }
    ],
    parentLabel: 'Performance',
    icon: 'Storage',
    component: Repos,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/categories',
    label: 'Work Categories',
    title: 'Work Categories',
    pageName: 'Work Categories',
    pageSubtitle:
      'A detailed view of engineering effort distribution and trends by work type.',
    parentLabel: 'Performance',
    icon: 'Category',
    component: Categories,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/projects',
    label: 'Projects',
    pageSubtitle:
      'A detailed view of engineering effort distribution and trends across projects.',
    pageActions: [
      {
        label: 'Manage Projects',
        href: 'https://panel.gitdotme.com/settings/project-management'
      }
    ],
    parentLabel: 'Performance',
    icon: 'AccountTree',
    component: Projects,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/tags',
    label: 'Tags',
    pageSubtitle:
      'A detailed view of engineering effort trends aggregated by developer tags.',
    pageActions: [
      {
        label: 'Create Tags',
        href: 'https://panel.gitdotme.com/settings/tags'
      },
      {
        label: 'Assign Tags',
        href: 'https://panel.gitdotme.com/settings/developers'
      }
    ],
    parentLabel: 'Performance',
    icon: 'Label',
    component: TagsDashboard,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/ai-leverage/developers',
    label: 'Developers',
    title: 'AI Leverage · Developers',
    pageSubtitle:
      'Compare human and AI contribution across developers by showing the human effort required alongside the effort saved through AI and automation.',
    parentLabel: 'AI Leverage',
    icon: 'AutoGraph',
    component: AiLeverageDevelopers,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/ai-leverage/repositories',
    label: 'Repositories',
    title: 'AI Leverage · Repositories',
    pageSubtitle:
      'Compare human and AI contribution across repositories by showing the human effort required alongside the effort saved through AI and automation.',
    parentLabel: 'AI Leverage',
    icon: 'Storage',
    component: AiLeverageRepositories,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/ai-leverage/projects',
    label: 'Projects',
    title: 'AI Leverage · Projects',
    pageSubtitle:
      'Compare human and AI contribution across projects by showing the human effort required alongside the effort saved through AI and automation.',
    parentLabel: 'AI Leverage',
    icon: 'AccountTree',
    component: AiLeverageProjects,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/ai-adoption',
    label: 'Legacy AI Leverage',
    component: LegacyAiAdoptionRedirect,
    hideFromSidebar: true,
    hidePageHeader: true,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },

  {
    path: '/retention',
    label: 'Effort Survival Cohort',
    pageName: 'Effort Survival Cohort',
    pageSubtitle:
      'Cohort-based view of how much past engineering effort remains effective over time',
    title: 'Effort Survival Cohort',
    icon: 'Timeline',
    component: Retention,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/developer-report',
    label: 'Developers',
    pageSubtitle:
      'Unified view of selected developer trends, retention, insights, and activity feed.',
    pageActions: [
      {
        label: 'Manage Developers',
        href: '/settings/developers'
      }
    ],
    parentLabel: 'Reports',
    icon: 'PersonSearch',
    component: DeveloperReport,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/repo-report',
    label: 'Repositories',
    title: 'Repositories',
    pageName: 'Repositories',
    pageSubtitle:
      'Unified view of selected repo trends, retention, ownership distribution, and activity feed.',
    pageActions: [
      {
        label: 'Manage Repositories',
        href: '/settings/repositories'
      }
    ],
    parentLabel: 'Reports',
    icon: 'Storage',
    component: RepoReport,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/project-report',
    label: 'Projects',
    pageSubtitle:
      'Unified view of selected project trends, retention, ownership distribution, and activity feed.',
    pageActions: [
      {
        label: 'Manage Projects',
        href: '/settings/project-management'
      }
    ],
    parentLabel: 'Reports',
    icon: 'AccountTree',
    component: ProjectReport,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },

  /* HIDDEN */
  {
    path: '/commit-details',
    label: 'Commit Details',
    pageSubtitle:
      'Commit-level details to track effort spent across features, epics, and initiatives.',
    parentLabel: 'Performance',
    component: UserDetail,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },

  /* MANAGEMENT */
  {
    path: '/settings/developers',
    label: 'Developers',
    pageSubtitle:
      'Manage developer identities, emails, and tag assignments used across analytics.',
    parentLabel: 'Management',
    icon: 'ManageAccounts',
    component: Developers,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/repositories',
    label: 'Repositories',
    pageSubtitle:
      'Adjust how much each repository contributes to effort calculations.',
    parentLabel: 'Management',
    icon: 'ManageAccounts',
    component: Repositories,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN]
  },
  {
    path: '/settings/tags',
    label: 'Tags',
    pageSubtitle:
      'Organize tags into groups to manage developer segments and filter your analyses.',
    parentLabel: 'Management',
    icon: 'ManageAccounts',
    component: Tags,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN]
  },
  {
    path: '/settings/project-management',
    label: 'Projects',
    pageSubtitle:
      'Organize repositories into projects for aggregated effort and performance insights.',
    parentLabel: 'Management',
    icon: 'ManageAccounts',
    component: ProjectManagement,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN]
  },
  {
    path: '/settings/configurations',
    label: 'Configurations',
    parentLabel: 'Management',
    icon: 'ManageAccounts',
    component: Configurations,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN]
  },

  /* ACCOUNT SETTINGS */
  {
    path: '/settings/user',
    label: 'User Profile',
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: Settings,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/settings/change-password',
    label: 'Change Password',
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: ChangePassword,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/settings/invite-user',
    label: 'Invite Users',
    pageSubtitle: 'Invite users by selecting their role.',
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: InviteUser,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/manage-user-access',
    label: 'User Management',
    pageSubtitle:
      'Update user roles, names, and access for everyone in your workspace.',
    parentLabel: 'Account Settings',
    icon: 'Group',
    component: ManageUserAccess,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/support',
    label: 'Support',
    pageSubtitle:
      'Open a support request, continue the conversation, and track the latest updates from GitMe Support.',
    pageActions: [
      {
        label: 'Docs',
        href: 'https://docs.gitdotme.com/docs/intro'
      }
    ],
    parentLabel: 'Account Settings',
    icon: 'SupportAgent',
    hideFromSidebar: true,
    component: CustomerSupportPage,
    roles: [ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER]
  },
  {
    path: '/settings/integrations',
    label: 'Integrations',
    parentLabel: 'Account Settings',
    icon: 'Extension',
    component: Integrations,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/integrations/self-hosted/:provider/connect',
    label: 'Connect Self-Hosted Integration',
    parentLabel: 'Account Settings',
    icon: 'Extension',
    component: SelfHostedIntegrationConnect,
    roles: [ROLES.ACCOUNT_ADMIN],
    hideFromSidebar: true
  },
  {
    path: '/settings/plan-configuration',
    label: 'Plan Configuration',
    pageSubtitle:
      'Review your current token usage and choose the right monthly capacity.',
    pageActions: [
      {
        label: 'View usage history',
        href: '/settings/usage-history'
      },
      {
        label: 'Manage billing',
        href: '/settings/card-management'
      }
    ],
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: PlanConfiguration,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/card-management',
    label: 'Billing & Payments',
    pageSubtitle:
      'Manage your billing details, payment methods, and invoices in one place.',
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: CardManagement,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/usage-history',
    label: 'Usage History',
    pageSubtitle:
      'Track daily token consumption, billing cycles, and overage charges.',
    parentLabel: 'Account Settings',
    icon: 'AccountCircle',
    component: PaymentSummary,
    roles: [ROLES.ACCOUNT_ADMIN]
  },
  {
    path: '/settings/historical-analysis',
    label: 'Historical Analysis',
    pageSubtitle:
      'Extend your analysis window into older commit history with a one-time historical backfill.',
    parentLabel: 'Account Settings',
    icon: 'HistoryEdu',
    component: HistoricalAnalysis,
    roles: [ROLES.ACCOUNT_ADMIN]
  }
];

/* ================= SIDEBAR ORDER ================= */
const SIDEBAR_ORDER = [
  'Executive Summary',
  'DevFeed',
  'Ask AI',
  'AI Leverage',
  'Reports',
  'Productivity Scores',
  'Performance',
  'Effort Survival Cohort',
  'Management',
  'Account Settings'
];

/* ================= SIDEBAR BUILDER ================= */
export const getSidebarItems = (
  userRole,
  { notificationsByPath = {}, notificationCountsByPath = {} } = {}
) => {
  const normalizedRole = resolveUserRole(userRole);
  const routes = UNIFIED_ROUTES.filter(r => {
    const roleForRoute = r.path.startsWith('/settings')
      ? normalizedRole
      : resolveDashboardRole(normalizedRole);

    return hasAccess(roleForRoute, r.roles);
  });

  const sidebar = [];

  SIDEBAR_ORDER.forEach(section => {
    const groupRoutes = routes.filter(
      r => r.parentLabel === section && !r.hideFromSidebar
    );

    if (groupRoutes.length) {
      const shouldCollapseChildBadges = section === 'Reports';
      const groupBadge =
        shouldCollapseChildBadges && groupRoutes.some(r => Boolean(r.badge))
          ? 'NEW'
          : undefined;

      sidebar.push({
        label: section,
        icon: groupRoutes[0].icon,
        badge: groupBadge,
        nodes: groupRoutes.map(r => ({
          label: r.label,
          path: r.path,
          badge: shouldCollapseChildBadges ? undefined : r.badge,
          notification:
            Boolean(notificationsByPath[r.path]) ||
            Number(notificationCountsByPath[r.path] || 0) > 0,
          notificationCount: Number(notificationCountsByPath[r.path] || 0)
        }))
      });
    } else {
      const route = routes.find(r => r.label === section);
      if (route) {
        sidebar.push({
          label: route.label,
          path: route.path,
          icon: route.icon,
          badge: route.badge,
          notification:
            Boolean(notificationsByPath[route.path]) ||
            Number(notificationCountsByPath[route.path] || 0) > 0,
          notificationCount: Number(notificationCountsByPath[route.path] || 0)
        });
      }
    }
  });

  return sidebar;
};

/* ================= ROUTE HELPERS ================= */
export const getDashboardRoutes = userRole => {
  const normalizedRole = resolveUserRole(userRole);

  return UNIFIED_ROUTES.filter(
    r =>
      !r.path.startsWith('/settings') &&
      hasAccess(resolveDashboardRole(normalizedRole), r.roles)
  );
};

export const getSettingsRoutes = userRole => {
  const normalizedRole = resolveUserRole(userRole);

  return UNIFIED_ROUTES.filter(
    r => r.path.startsWith('/settings') && hasAccess(normalizedRole, r.roles)
  );
};
